import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, Alert, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid
} from '@mui/material';
import {
  CheckCircle as ApproveIcon, Cancel as DenyIcon, Visibility as ViewIcon
} from '@mui/icons-material';
import { getEnrollments, updateEnrollmentStatus } from '../api';

const STATUS_COLORS = {
  pending: 'warning',
  active: 'success',
  cancelled: 'error',
  declined: 'error',
  cobra: 'info',
};

const fmt = (val) => parseFloat(val || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

export default function EnrollmentAdmin() {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);

  const fetchEnrollments = async () => {
    try {
      setLoading(true);
      const res = await getEnrollments(statusFilter || undefined);
      setEnrollments(res.data);
    } catch (err) {
      setError('Failed to load enrollments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEnrollments(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (id, newStatus) => {
    try {
      await updateEnrollmentStatus(id, newStatus);
      setSuccess(`Enrollment ${newStatus === 'active' ? 'approved' : newStatus}`);
      fetchEnrollments();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update enrollment');
    }
  };

  const pendingCount = enrollments.filter(e => e.status === 'pending').length;

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Enrollment Review
          {pendingCount > 0 && (
            <Chip label={`${pendingCount} pending`} color="warning" size="small" sx={{ ml: 1 }} />
          )}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Filter Status</InputLabel>
          <Select value={statusFilter} label="Filter Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
            <MenuItem value="declined">Declined</MenuItem>
            <MenuItem value="cobra">COBRA</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Employee</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Coverage</TableCell>
              <TableCell align="right">Employee Cost</TableCell>
              <TableCell align="right">Employer Cost</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {enrollments.map(enrollment => (
              <TableRow key={enrollment.id} sx={{
                bgcolor: enrollment.status === 'pending' ? 'warning.50' : undefined
              }}>
                <TableCell>
                  <Typography fontWeight="medium">
                    {enrollment.first_name} {enrollment.last_name}
                  </Typography>
                </TableCell>
                <TableCell>{enrollment.plan_name}</TableCell>
                <TableCell>
                  <Chip label={enrollment.plan_category} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{enrollment.coverage_level?.replace(/_/g, ' ')}</TableCell>
                <TableCell align="right">{fmt(enrollment.employee_contribution)}</TableCell>
                <TableCell align="right">{fmt(enrollment.employer_contribution)}</TableCell>
                <TableCell>
                  {enrollment.effective_date ? new Date(enrollment.effective_date).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>{enrollment.period_name || '—'}</TableCell>
                <TableCell>
                  <Chip label={enrollment.status} color={STATUS_COLORS[enrollment.status] || 'default'}
                        size="small" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <IconButton size="small" title="View details"
                      onClick={() => { setSelectedEnrollment(enrollment); setViewDialog(true); }}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                    {enrollment.status === 'pending' && (
                      <>
                        <IconButton size="small" color="success" title="Approve"
                          onClick={() => handleStatusChange(enrollment.id, 'active')}>
                          <ApproveIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" title="Decline"
                          onClick={() => handleStatusChange(enrollment.id, 'declined')}>
                          <DenyIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                    {enrollment.status === 'active' && (
                      <IconButton size="small" color="error" title="Cancel enrollment"
                        onClick={() => handleStatusChange(enrollment.id, 'cancelled')}>
                        <DenyIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {enrollments.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  {statusFilter ? `No ${statusFilter} enrollments` : 'No enrollments yet'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* View Detail Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Enrollment Details</DialogTitle>
        <DialogContent>
          {selectedEnrollment && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Employee</Typography>
                <Typography>{selectedEnrollment.first_name} {selectedEnrollment.last_name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Status</Typography>
                <Box><Chip label={selectedEnrollment.status} color={STATUS_COLORS[selectedEnrollment.status]} size="small" /></Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Plan</Typography>
                <Typography>{selectedEnrollment.plan_name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Category</Typography>
                <Typography>{selectedEnrollment.plan_category}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Coverage Level</Typography>
                <Typography>{selectedEnrollment.coverage_level?.replace(/_/g, ' ')}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Effective Date</Typography>
                <Typography>
                  {selectedEnrollment.effective_date ? new Date(selectedEnrollment.effective_date).toLocaleDateString() : 'Not set'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Employee Contribution</Typography>
                <Typography>{fmt(selectedEnrollment.employee_contribution)}/mo</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Employer Contribution</Typography>
                <Typography>{fmt(selectedEnrollment.employer_contribution)}/mo</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Enrollment Period</Typography>
                <Typography>{selectedEnrollment.period_name || 'None (direct enrollment)'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Submitted</Typography>
                <Typography>{new Date(selectedEnrollment.created_at).toLocaleString()}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedEnrollment?.status === 'pending' && (
            <>
              <Button color="error" onClick={() => { handleStatusChange(selectedEnrollment.id, 'declined'); setViewDialog(false); }}>
                Decline
              </Button>
              <Button variant="contained" color="success" onClick={() => { handleStatusChange(selectedEnrollment.id, 'active'); setViewDialog(false); }}>
                Approve
              </Button>
            </>
          )}
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
